import React from 'react'
import { router } from '@inertiajs/react'
import { useReturnTo, dataWithReturnTo } from '@/contexts/ReturnToContext'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import { IssueMessageForm } from './IssueMessageForm'
import { Markdown } from '../Markdown'
import { AttachmentItem, AttachmentData } from './AttachmentItem'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface IssueMessageData {
  id: number
  message: string
  status: string
  agent: AgentRef
  attachments: AttachmentData[]
  created_at: string
}

interface IssueData {
  id: number
  message: string
  status: string
  issue_type: string
  assignee: AgentRef
  requestor: AgentRef
  messages: IssueMessageData[]
  attachments: AttachmentData[]
  created_at: string
  updated_at: string
}

interface Props {
  issue: IssueData
  boardSlug: string
  taskId: number
  issueType: 'clarification' | 'review'
}

const AVATAR_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
  '#c2185b', '#0097a7', '#f57c00', '#455a64',
]

function agentColor(username: string): string {
  let hash = 0
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function AgentAvatar({ agent }: { agent: AgentRef }) {
  return (
    <Avatar
      sx={{
        width: 28,
        height: 28,
        fontSize: '0.8rem',
        fontWeight: 600,
        bgcolor: agentColor(agent.username),
        flexShrink: 0,
      }}
    >
      {agent.username[0].toUpperCase()}
    </Avatar>
  )
}

export function IssueItem({ issue, boardSlug, taskId, issueType }: Props) {
  const returnTo = useReturnTo()
  const isDraft = issue.status === 'draft'
  const isPending = issue.status === 'pending' || isDraft

  const issueSegment = issueType === 'clarification' ? 'clarifications' : 'review_items'
  const paramKey = issueType === 'clarification' ? 'clarification' : 'review_item'

  function handleResolve() {
    router.patch(`/tasks/boards/${boardSlug}/tasks/${taskId}/${issueSegment}/${issue.id}`, dataWithReturnTo({
      [paramKey]: { status: 'resolved' },
    }, returnTo))
  }

  function handleReopen() {
    router.patch(`/tasks/boards/${boardSlug}/tasks/${taskId}/${issueSegment}/${issue.id}`, dataWithReturnTo({
      [paramKey]: { status: 'pending' },
    }, returnTo))
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: isPending ? 'warning.light' : 'divider',
        borderRadius: 1.5,
      }}
    >
      {/* Meta line: status + asked by + assigned to + date */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <Chip
          label={isDraft ? 'Draft' : (issue.status === 'pending' ? 'Pending' : 'Resolved')}
          size="small"
          color={isDraft ? 'default' : (issue.status === 'pending' ? 'warning' : 'success')}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            ...(isDraft ? { backgroundColor: '#FFC107', color: '#000' } : {}),
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Asked by
        </Typography>
        <Chip
          label={issue.requestor.username}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.75rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          · Assigned to
        </Typography>
        <Chip
          label={issue.assignee.username}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.75rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          · {new Date(issue.created_at).toLocaleDateString()}
        </Typography>
      </Box>

      {/* All messages (including the original) displayed uniformly */}
      {issue.messages.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {issue.messages.map((msg) => (
            <Box key={msg.id} sx={{ display: 'flex', gap: 1.5 }}>
              <AgentAvatar agent={msg.agent} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {msg.agent.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(msg.created_at).toLocaleDateString()}
                  </Typography>
                  {msg.status === 'draft' && (
                    <Chip
                      label="Draft"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: '#FFC107',
                        color: '#000',
                      }}
                    />
                  )}
                </Box>
                <Markdown variant="body2">
                  {msg.message}
                </Markdown>
                {msg.attachments && msg.attachments.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    {msg.attachments.map((a) => (
                      <AttachmentItem
                        key={a.id}
                        attachment={a}
                        boardSlug={boardSlug}
                        taskId={taskId}
                        deleteUrl={`/tasks/boards/${boardSlug}/tasks/${taskId}/${issueSegment}/${issue.id}/messages/${msg.id}/attachments/${a.id}`}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Reply form */}
      {isPending && (
        <Box sx={{ mt: 1.5 }}>
          <IssueMessageForm
            boardSlug={boardSlug}
            taskId={taskId}
            issueId={issue.id}
            issueType={issueType}
          />
        </Box>
      )}

      {/* Resolve / Reopen button at bottom */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        {isPending ? (
          <Button size="small" variant="text" color="success" onClick={handleResolve}>
            Resolve
          </Button>
        ) : (
          <Button size="small" variant="text" color="warning" onClick={handleReopen}>
            Reopen
          </Button>
        )}
      </Box>
    </Paper>
  )
}
